import React, { useState, useEffect } from 'react';
import { Calendar, Phone, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { contactData } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSection = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [busyDates, setBusyDates] = useState([]);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Fetch availability when month changes
  useEffect(() => {
    fetchAvailability();
  }, [currentDate]);

  const fetchAvailability = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await fetch(`${BACKEND_URL}/api/calendar/availability?month=${month}&year=${year}`);
      const data = await response.json();
      setBusyDates(data.busy_dates || []);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  // Calendar navigation
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isBusy = busyDates.some(b => b.date === dateStr);
      const isPast = new Date(dateStr) < new Date(new Date().toDateString());
      const isToday = new Date().toDateString() === new Date(dateStr).toDateString();
      
      days.push({
        day,
        date: dateStr,
        isBusy,
        isPast,
        isToday
      });
    }
    
    return days;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const calendarDays = generateCalendarDays();

  return (
    <section id="booking" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/20 rounded-full px-4 py-2 mb-6">
            <Calendar className="w-4 h-4 text-gold-400" />
            <span className="text-gold-300 text-sm font-medium">Book Your Date</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Schedule Your <span className="text-gold-400">Event</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto mb-4">
            Check availability below, then select a date and time to book Alexander Richard for your next event.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-stone-400">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gold-400" />
              <span>AI Receptionist: </span>
              <a href={`tel:${contactData.receptionist.phone}`} className="text-gold-400 hover:text-gold-300">
                {contactData.receptionist.phone}
              </a>
            </div>
            <span className="hidden sm:inline text-stone-600">|</span>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-400" />
              <span>Real Human: </span>
              <a href={`tel:${contactData.human.phone}`} className="text-green-400 hover:text-green-300">
                {contactData.human.phone}
              </a>
            </div>
          </div>
        </div>

        {/* Availability Calendar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 text-center">Availability at a Glance</h3>
            
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-stone-400" />
              </button>
              <h4 className="text-white font-medium">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h4>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-stone-400" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day, index) => (
                <div key={index} className="text-center text-stone-500 text-xs py-1 font-medium">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayInfo, index) => (
                <div
                  key={index}
                  className={`
                    aspect-square flex flex-col items-center justify-center rounded-lg text-sm relative
                    ${!dayInfo.day ? 'bg-transparent' : ''}
                    ${dayInfo.isPast ? 'text-stone-600' : 'text-stone-300'}
                    ${dayInfo.isToday ? 'ring-1 ring-gold-500' : ''}
                    ${dayInfo.day ? 'bg-stone-800/30' : ''}
                  `}
                >
                  {dayInfo.day && (
                    <>
                      <span className={`text-xs ${dayInfo.isToday ? 'text-gold-400 font-bold' : ''}`}>
                        {dayInfo.day}
                      </span>
                      {dayInfo.isBusy && !dayInfo.isPast && (
                        <div className="absolute bottom-1">
                          <div className="w-2 h-2 rounded-full bg-gold-500" title="Booked" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-stone-800">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gold-500" />
                <span className="text-stone-400 text-xs">Booked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-stone-800/30 ring-1 ring-gold-500" />
                <span className="text-stone-400 text-xs">Today</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendly Embed */}
        <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-4 sm:p-6 overflow-hidden">
          <div 
            className="calendly-inline-widget" 
            data-url="https://calendly.com/csnr-booking?hide_gdpr_banner=1&background_color=1c1917&text_color=ffffff&primary_color=d4af37"
            style={{ 
              minWidth: '320px', 
              height: '700px',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          />
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-stone-500 text-sm">
            After booking, you'll receive a confirmation email with all the details.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
